
import { CharacterCondition, CharacterGift, CharacterItem, CharacterMagicItemKeyword } from "@dndModels/character_feature_source";
import { DataType } from "@dndModels/Types";
import { ModelBase, PlayerObject, UpsertableBase } from "@models";

/**
 * This is an object that allows for DMs and players to send items or conditions to players in a campaign.
 */
export class Receivable extends PlayerObject {
  data_type: DataType = "receivable";
  conversation_id = "";
  char_id = ""; // id of char who is receiving it
  campaign_id = "";
  user_ids: string[] = [];
  admin = false;
  from_id = ""; // DM or id of char who sent it
  sent_dt = 0;
  received_dt = 0;
  message = "";
  temp = false;
  items: ReceivableItem[] = [];
  origin_id = ""; // This is used to keep the browser it was sent from from reacting to it.
  type: "Chat" | "Functional" | "Mail" = "Functional";
  seen_by: string[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.conversation_id = obj.conversation_id ? obj.conversation_id : "";
      this.char_id = obj.char_id ? obj.char_id : "";
      this.campaign_id = obj.campaign_id ? obj.campaign_id : "";
      this.user_ids = obj.user_ids ? obj.user_ids : [];
      this.admin = obj.admin ? obj.admin : false;
      this.from_id = obj.from_id ? obj.from_id : "";
      this.sent_dt = obj.sent_dt ? obj.sent_dt : 0;
      this.received_dt = obj.received_dt ? obj.received_dt : 0;
      this.message = obj.message ? obj.message : "";
      this.temp = obj.temp ? obj.temp : false;
      this.items = obj.items ? obj.items.map((item: any) => new ReceivableItem(item)) : [];
      this.origin_id = obj.origin_id ? obj.origin_id : "";
      this.type = obj.type ? obj.type : "Functional";
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      conversation_id: this.conversation_id,
      char_id: this.char_id,
      campaign_id: this.campaign_id,
      user_ids: this.user_ids,
      admin: this.admin,
      from_id: this.from_id,  
      sent_dt: this.sent_dt,
      received_dt: this.received_dt,
      message: this.message,
      temp: this.temp,
      items: this.items.map((item: ReceivableItem) => item.toDBObj()),
      origin_id: this.origin_id,
      type: this.type,
    };
  }

  clone(): Receivable {
    const clone = new Receivable();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: Receivable): void {
    super.copy(copyMe);
    this.conversation_id = copyMe.conversation_id;
    this.char_id = copyMe.char_id;
    this.campaign_id = copyMe.campaign_id;
    this.user_ids = [...copyMe.user_ids];
    this.admin= copyMe.admin;
    this.from_id = copyMe.from_id;
    this.sent_dt = copyMe.sent_dt;
    this.received_dt = copyMe.received_dt;
    this.message = copyMe.message;
    this.items = copyMe.items.map((item: ReceivableItem) => item.clone());
    this.origin_id = copyMe.origin_id;
    this.type = copyMe.type;
  }
}

export type ReceivableObjectType = CharacterItem | CharacterCondition | CharacterGift | CharacterMagicItemKeyword;

export const isReceivableObject = (obj: any): obj is ReceivableObjectType => {
  return obj instanceof CharacterItem ||
    obj instanceof CharacterCondition ||
    obj instanceof CharacterGift ||
    obj instanceof CharacterMagicItemKeyword;
}

/**
 * Changing this so it can take more details about the things being sent. 
 * For Item it will send CharacterItem, for Condition it will send CharacterCondition, etc.
 * 
 */
export class ReceivableItem {
  type: "Condition" | "Item" | "Gift" | "Item Enhancement" = "Item";
  obj?: ReceivableObjectType;

  constructor(obj?: any) {
    if (obj) {
      this.type = obj.type ? obj.type : "Item";
      if (obj.obj) {
        switch (this.type) {
          case "Condition": this.obj = new CharacterCondition(obj.obj); break;
          case "Gift": this.obj = new CharacterGift(obj.obj); break;
          case "Item": this.obj = new CharacterItem(obj.obj); break;
          case "Item Enhancement": this.obj = new CharacterMagicItemKeyword(obj.obj); break;
        }
      }
    }
  }

  toDBObj(): any {
    return {
      type: this.type,
      obj: this.obj ? this.obj.toDBObj() : undefined
    };
  }

  clone(): ReceivableItem {
    const clone = new ReceivableItem();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: ReceivableItem): void {
    this.type = copyMe.type;
    this.obj = copyMe.obj ? copyMe.obj.clone() : undefined;
  }
}

export class Conversation extends PlayerObject {
  data_type: DataType = "conversation";
  type: "Campaign" | "User" | "Admin" = "User";
  ids: string[] = []; // This is the ids of the Campaign or Users in the chat.
  last_message_time = 0;

  // Not saved.
  messages: Receivable[] = [];
  hidden = false;
  name = "";
  flashing = false;
  open = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.type = obj.type ? obj.type : "User";
      this.ids = obj.ids ? obj.ids : [];
      this.last_message_time = obj.last_message_time ? obj.last_message_time : 0;
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      type: this.type,
      ids: this.ids,
      last_message_time: this.last_message_time,
    };
  }

  clone(): Conversation {
    const clone = new Conversation();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: Conversation): void {
    this.type = copyMe.type;
    this.ids = [...copyMe.ids];
    this.messages = [...copyMe.messages];
    this.name = copyMe.name;
    this.flashing = copyMe.flashing;
    this.open = copyMe.open;
    this.last_message_time = copyMe.last_message_time;
  }
}

export class ConvoSeenTimes extends PlayerObject {
  data_type: DataType = "convo_seen_times";
  hash: { [convo_id: string]: number } = {};

  seen_time(convo_id: string) {
    const data = this.hash[convo_id];
    return data === undefined ? -1 : data;
  }

  setSeenTime(convo_id: string, time: number) {
    this.hash[convo_id] = time;
  }

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.hash = obj.hash ? obj.hash : {};
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      hash: this.hash,
    };
  }

  clone(): ConvoSeenTimes {
    const clone = new ConvoSeenTimes();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: ConvoSeenTimes): void {
    super.copy(copyMe);
    this.hash = {...copyMe.hash};
  }
}